import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFeePositionHistory } from '../../api/feeTier';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceDot } from 'recharts';
import StandaloneSection from '../shared/StandaloneSection';
import './BalanceHistoryChart.css';

type TimeRange = '7d' | '30d' | '90d';

interface ChartDataPoint {
  timestamp: number;
  qualifyingValue?: number;
  pricingTier: string;
  isTierTransition: boolean;
  date: string;
}

const RANGE_HOURS: Record<TimeRange, number> = {
  '7d': 7 * 24,
  '30d': 30 * 24,
  '90d': 90 * 24,
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const point: ChartDataPoint = payload[0].payload;
    return (
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1e293b' }}>{formatDate(point.date)}</p>
        <p style={{ color: '#1e293b', fontWeight: 600, fontSize: '0.95rem' }}>
          Tier: {point.pricingTier}{point.isTierTransition ? ' (transition)' : ''}
        </p>
        {point.qualifyingValue !== undefined && (
          <p style={{ color: '#1e293b', fontSize: '0.9rem' }}>Volume: {formatCurrency(point.qualifyingValue)}</p>
        )}
      </div>
    );
  }
  return null;
};

/**
 * Fee-Tier & Volume Tracker history chart (spec REQ-010). Mirrors BalanceHistoryChart's
 * StandaloneSection + Recharts AreaChart shape (sibling-first UI reuse), simplified to a single
 * time-range selector since the backend serves raw hourly snapshots, not rollup granularities.
 */
const FeeTierHistoryChart: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState<TimeRange>('30d');
  const hours = RANGE_HOURS[selectedRange];

  const { data, isLoading, isError } = useQuery({
    queryKey: ['feeTier', 'position', 'history', selectedRange, hours],
    queryFn: () => getFeePositionHistory(hours),
    retry: 2,
  });

  const chartData: ChartDataPoint[] = useMemo(() => {
    return (data || [])
      .map((entry) => ({
        timestamp: new Date(entry.capturedAt).getTime(),
        qualifyingValue: entry.qualifyingValue ? parseFloat(entry.qualifyingValue) : undefined,
        pricingTier: entry.pricingTier,
        isTierTransition: entry.tierTransition,
        date: entry.capturedAt,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [data]);

  const transitions = chartData.filter((d) => d.isTierTransition);

  if (isLoading) {
    return (
      <StandaloneSection title="FEE TIER HISTORY">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading fee tier history...</p>
        </div>
      </StandaloneSection>
    );
  }

  if (isError) {
    return (
      <StandaloneSection title="FEE TIER HISTORY">
        <div className="error-message">
          <p>Unable to load fee tier history</p>
        </div>
      </StandaloneSection>
    );
  }

  return (
    <StandaloneSection title="FEE TIER HISTORY">
      <div className="balance-history-container">
        <div className="time-range-selector">
          <button className={`range-button ${selectedRange === '7d' ? 'active' : ''}`} onClick={() => setSelectedRange('7d')}>7D</button>
          <button className={`range-button ${selectedRange === '30d' ? 'active' : ''}`} onClick={() => setSelectedRange('30d')}>30D</button>
          <button className={`range-button ${selectedRange === '90d' ? 'active' : ''}`} onClick={() => setSelectedRange('90d')}>90D</button>
        </div>

        {chartData.length > 0 ? (
          <>
            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                  <defs>
                    <linearGradient id="qualifyingVolumeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8a8ced" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8a8ced" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12 }} minTickGap={30} interval="preserveStartEnd" />
                  <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="qualifyingValue"
                    stroke="#8a8ced"
                    strokeWidth={2}
                    fill="url(#qualifyingVolumeGradient)"
                    connectNulls={false}
                  />
                  {transitions.map((t) => (
                    <ReferenceDot
                      key={t.date}
                      x={t.date}
                      y={t.qualifyingValue ?? 0}
                      r={5}
                      fill="#f59e0b"
                      stroke="#1e293b"
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {transitions.length > 0 && (
              <div className="granularity-label">
                {transitions.length} tier transition{transitions.length > 1 ? 's' : ''} in this window (orange markers)
              </div>
            )}
          </>
        ) : (
          <div className="no-data">No fee tier history available for this time range</div>
        )}
      </div>
    </StandaloneSection>
  );
};

export default FeeTierHistoryChart;
